import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BigModal from '../../components/Modals/BigModal'
import MyFeedback from './MyFeedback'
import { openCrispChat } from '../../helpers/useCrisp'
import SupportRequestForm, {
  TICKET_CONFIGS,
  TICKET_TYPE_ORDER,
} from './SupportRequestForm'
import DocsSearch from '../../docs/components/DocsSearch'

import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'

import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded'
import ChatRoundedIcon from '@mui/icons-material/ChatRounded'
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import DeveloperBoardRoundedIcon from '@mui/icons-material/DeveloperBoardRounded'
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'

const BRAND = {
  dark: '#101827',
  navy: '#1A2230',
  slate: '#242F40',
  teal: '#239AA4',
  tealLight: '#46D7E1',
  bg: '#F5F7FB',
}

export default function Support() {
  const [popup, setPopup] = useState(null)
  const navigate = useNavigate()

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 3 },
        py: { xs: 2, md: 3 },
        my: -1,
        mx: -2,
      }}
    >
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 5,
              p: { xs: 3, md: 5 },
              color: 'white',
              border: '1px solid rgba(255,255,255,0.18)',
              boxShadow: '0 30px 90px rgba(15,23,42,0.25)',
              background: `
              radial-gradient(circle at 16% 8%, rgba(70,215,225,0.22), transparent 24%),
              radial-gradient(circle at 92% 18%, rgba(212,20,90,0.16), transparent 22%),
              linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.slate} 52%, ${BRAND.dark} 100%)
            `,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: 260,
                height: 260,
                right: -80,
                top: -80,
                borderRadius: '50%',
                background: 'rgba(70,215,225,0.13)',
                filter: 'blur(2px)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: 170,
                height: 170,
                right: 90,
                bottom: -80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.07)',
              }}
            />

            <Grid container spacing={4} alignItems="center" position="relative">
              <Grid item xs={12} lg={7}>
                <Chip
                  icon={
                    <AutoAwesomeRoundedIcon
                      sx={{ color: `${BRAND.tealLight} !important` }}
                    />
                  }
                  label="AmbuNet Support"
                  sx={{
                    mb: 2,
                    backgroundColor: 'rgba(255,255,255,0.10)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.18)',
                    fontWeight: 900,
                    backdropFilter: 'blur(12px)',
                  }}
                />

                <Typography
                  variant="h3"
                  fontWeight={950}
                  color="white"
                  sx={{
                    letterSpacing: '-0.04em',
                    fontSize: { xs: '2.15rem', md: '3.25rem' },
                    lineHeight: 1,
                  }}
                >
                  How can we help?
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    mt: 2,
                    color: 'rgba(255,255,255,0.72)',
                    maxWidth: 680,
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                  }}
                >
                  Submit requests, report bugs, suggest improvements, and track
                  updates from one clean support hub.
                </Typography>

                <Box sx={{ mt: 3, maxWidth: 640 }}>
                  <DocsSearch />
                </Box>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 4,
                    border: '1px solid rgba(255,255,255,0.16)',
                    background: 'rgba(255,255,255,0.08)',
                    backdropFilter: 'blur(18px)',
                    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                  }}
                >
                  <Stack spacing={2}>
                    <SupportNotice
                      icon={<EngineeringRoundedIcon />}
                      title="Technical support"
                      text="For AmbuNet bugs, feature requests, account queries, and product support."
                      color={BRAND.tealLight}
                    />
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
                    <SupportNotice
                      icon={<LocalHospitalRoundedIcon />}
                      title="Operational issue?"
                      text="For live operational matters, contact your organisation’s on-call team directly."
                      color="#FBBF24"
                    />
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />
                    <Stack color="rgba(255,255,255,0.68)">
                      <Stack
                        // justifyContent="space-between"
                        gap={1}
                        direction="row"
                      >
                        <Typography
                          variant="caption"
                          fontWeight="bold"
                          color="rgba(255,255,255,0.68)"
                        >
                          Version:
                        </Typography>
                        <Typography variant="caption" textAlign="center">
                          {process.env.AMBUNET_APP_VERSION}-
                          {process.env.AMBUNET_BUILD_NUMBER}
                        </Typography>
                      </Stack>
                      <Typography variant="caption">
                        We may ask you for this when discussing a support
                        request.
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} lg={8} display="flex">
          <SurfaceCard>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              spacing={1}
              mb={2.5}
            >
              <Box>
                <Typography variant="h5" fontWeight={950} color="anBlue.main">
                  Raise a ticket
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One button for everything. You choose what it is about on
                  the next step, so it reaches the right people straight away.
                </Typography>
              </Box>
            </Stack>

            <RequestCard
              title="Raise a ticket"
              description="General enquiries, bugs, feature requests and clinical safety incidents."
              icon={ConfirmationNumberRoundedIcon}
              color={BRAND.teal}
              bg="rgba(35,154,164,0.12)"
              onClick={() => setPopup('choose')}
            />

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mt={2}>
              {TICKET_TYPE_ORDER.map((key) => {
                const option = TICKET_CONFIGS[key]
                const OptionIcon = option.icon
                return (
                  <Chip
                    key={key}
                    icon={<OptionIcon />}
                    label={option.label}
                    onClick={() => setPopup(key)}
                    variant="outlined"
                    sx={{
                      fontWeight: 800,
                      borderColor: option.color,
                      color: option.color,
                      '& .MuiChip-icon': { color: option.color },
                    }}
                  />
                )
              })}
            </Stack>

            <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
              A clinical safety incident is anything where AmbuNet could have,
              or did, contribute to patient harm, a near miss, or a lost or
              wrong clinical record. It goes straight to our Clinical Safety
              Officer.
            </Typography>
          </SurfaceCard>
        </Grid>

        <Grid item xs={12} lg={4} display="flex">
          <SurfaceCard>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 3,
                background:
                  'linear-gradient(135deg, rgba(35,154,164,0.10), rgba(70,215,225,0.06))',
                border: '1px solid rgba(35,154,164,0.18)',
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                  <AutoAwesomeRoundedIcon sx={{ color: 'contrast.dark' }} />
                  <Typography variant="h5" color="contrast.dark">
                    Stay up to date
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Discover the latest AmbuNet features, improvements, bug fixes
                  and upcoming developments.
                </Typography>

                <Button
                  color="contrast"
                  onClick={() => navigate('/updates')}
                  fullWidth
                  variant="contained"
                >
                  View Updates
                </Button>
              </Stack>
            </Paper>

            <Stack spacing={1.2}>
              <SubtleContactButton
                icon={<ChatRoundedIcon />}
                title="Live chat"
                text="Chat with our team"
                onClick={openCrispChat}
              />
              <SubtleContactButton
                icon={<PhoneRoundedIcon />}
                title="Phone support"
                text="For urgent account issues"
                onClick={() => {
                  window.location.href = 'tel:+443300570620'
                }}
              />
            </Stack>
          </SurfaceCard>
        </Grid>
        <Grid item xs={12}>
          <SurfaceCard>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={2}
              mb={2}
            >
              <Box>
                <Typography variant="h5" fontWeight={950} color="anBlue.main">
                  My support requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track the enquiries, bugs, feature requests and safety
                  incidents you have raised.
                </Typography>
              </Box>

              <Button
                variant="outlined"
                endIcon={<DeveloperBoardRoundedIcon />}
                onClick={() => navigate('/updates')}
                sx={{
                  borderRadius: 3,
                  textTransform: 'none',
                  fontWeight: 900,
                  borderColor: 'rgba(35,154,164,0.35)',
                  color: BRAND.teal,
                  '&:hover': {
                    borderColor: BRAND.teal,
                    bgcolor: 'rgba(35,154,164,0.06)',
                  },
                }}
              >
                See What's New & Feature Requests
              </Button>
            </Stack>

            <MyFeedback />
          </SurfaceCard>
        </Grid>
      </Grid>

      <BigModal
        open={!!popup}
        onClose={() => setPopup(null)}
        style={{
          p: 0,
          borderRadius: 4,
          overflow: 'hidden',
          maxWidth: 760,
          width: '95%',
        }}
      >
        {popup && (
          <SupportRequestForm type={popup} onClose={() => setPopup(null)} />
        )}
      </BigModal>
    </Box>
  )
}

function SurfaceCard({ children, sx = {} }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2.25, md: 3 },
        borderRadius: 5,
        border: '1px solid rgba(15,23,42,0.08)',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.88))',
        boxShadow: '0 18px 60px rgba(15,23,42,0.08)',
        backdropFilter: 'blur(14px)',
        ...sx,
      }}
    >
      {children}
    </Paper>
  )
}

function RequestCard({ title, description, icon: Icon, color, bg, onClick }) {
  return (
    <Button
      fullWidth
      onClick={onClick}
      sx={{
        p: 0,
        textTransform: 'none',
        color: 'inherit',
        borderRadius: 3,
        '&:hover': { backgroundColor: 'transparent' },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 2,
          borderRadius: 3,
          border: '1px solid rgba(15,23,42,0.08)',
          background: '#fff',
          boxShadow: '0 10px 28px rgba(15,23,42,0.05)',
          transition: 'all 0.18s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            borderColor: color,
            boxShadow: '0 18px 42px rgba(15,23,42,0.10)',
          },
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: bg,
              color,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 28 }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <Typography fontWeight={950} sx={{ fontSize: '1.05rem' }}>
              {title}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
              {description}
            </Typography>
          </Box>

          <ArrowForwardRoundedIcon sx={{ color, fontSize: 24 }} />
        </Stack>
      </Paper>
    </Button>
  )
}

function SupportNotice({ icon, title, text, color }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          bgcolor: 'rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography fontWeight={950} color="white">
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)' }}>
          {text}
        </Typography>
      </Box>
    </Stack>
  )
}

function SubtleContactButton({ icon, title, text, onClick }) {
  return (
    <Button
      fullWidth
      onClick={onClick}
      sx={{
        justifyContent: 'flex-start',
        gap: 1.5,
        p: 1.5,
        borderRadius: 3,
        textAlign: 'left',
        textTransform: 'none',
        color: 'text.primary',
        border: '1px solid rgba(15,23,42,0.08)',
        background:
          'linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.88))',
        '&:hover': {
          transform: 'translateY(-2px)',
          backgroundColor: 'rgba(35,154,164,0.06)',
          borderColor: 'rgba(35,154,164,0.35)',
        },
        transition: 'all 0.2s ease',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(35,154,164,0.10)',
          color: BRAND.teal,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography fontWeight={950}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          {text}
        </Typography>
      </Box>

      <ArrowForwardRoundedIcon fontSize="small" />
    </Button>
  )
}
